import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';
import { TableSkeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Plus, Edit2, Trash2, X } from 'lucide-react';

interface TingkatRow {
  id: number;
  value: string;
  label: string;
  jenjang: string | null;
  urutan: number;
}

interface KelasRow {
  id: number;
  nama: string;
  tingkat: string | number;
  total_siswa?: number;
}

interface TahunAjaranSimple {
  id: number;
  is_active: boolean;
}

// Rombel di dalam form tingkat.
// - Rombel yang sudah ada di database: punya `id` dan `nama` lengkap (mis. "1 IBT A").
// - Rombel baru: hanya `suffix` (mis. "A"); nama lengkapnya = Nama Tingkat + suffix.
interface RombelDraft {
  id?: number;
  nama?: string;
  suffix?: string;
  terisi: number;
}

const KAPASITAS_DEFAULT = 32;

const naturalSort = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true });

export function TingkatKelasPage() {
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const queryClient = useQueryClient();

  const canCreate = hasPermission('kelas', 'tambah');
  const canEdit = hasPermission('kelas', 'ubah');
  const canDelete = hasPermission('kelas', 'hapus');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TingkatRow | null>(null);
  const [formData, setFormData] = useState({
    value: '',
    label: '',
    jenjang: '',
    urutan: 1,
  });
  const [rombelList, setRombelList] = useState<RombelDraft[]>([]);
  const [originalRombel, setOriginalRombel] = useState<RombelDraft[]>([]);
  const [loadingRombel, setLoadingRombel] = useState(false);
  const [rombelInput, setRombelInput] = useState('');
  const [jumlahOtomatis, setJumlahOtomatis] = useState(4);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['tingkat-kelas'],
    queryFn: async () => {
      const res = await api.get<TingkatRow[]>('/api/tingkat');
      return res.data || [];
    },
  });

  // Semua kelas, dipakai untuk menampilkan daftar rombel di tabel
  const { data: kelasAll = [] } = useQuery({
    queryKey: ['kelas-all'],
    queryFn: async () => {
      const res = await api.get<any>('/api/kelas?page=1&limit=100');
      const d: any = res.data;
      const rows = Array.isArray(d) ? d : d?.data ?? d?.items ?? [];
      return rows as KelasRow[];
    },
  });

  const { data: taList = [] } = useQuery({
    queryKey: ['tahun-ajaran'],
    queryFn: async () => {
      const res = await api.get<TahunAjaranSimple[]>('/api/tahun-ajaran');
      return res.data || [];
    },
  });
  const activeTa = taList.find((t) => t.is_active) || taList[0];

  const rombelPerTingkat = (value: string) =>
    kelasAll
      .filter((k) => String(k.tingkat) === String(value))
      .map((k) => k.nama)
      .sort(naturalSort);

  const fetchKelasTingkat = async (value: string): Promise<RombelDraft[]> => {
    const res = await api.get<any>(
      `/api/kelas?tingkat=${encodeURIComponent(value)}&page=1&limit=100`
    );
    const d: any = res.data;
    const rows = (Array.isArray(d) ? d : d?.data ?? d?.items ?? []) as KelasRow[];
    return rows
      .map((k) => ({ id: k.id, nama: k.nama, terisi: Number(k.total_siswa) || 0 }))
      .sort((a, b) => naturalSort(a.nama, b.nama));
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['tingkat-kelas'] });
    queryClient.invalidateQueries({ queryKey: ['kelas'] });
    queryClient.invalidateQueries({ queryKey: ['kelas-all'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
  };

  const namaRombel = (r: RombelDraft) =>
    r.id ? r.nama || '' : `${formData.label.trim()} ${r.suffix || ''}`.trim();

  // ---------- Daftar rombel di dalam modal ----------
  const addRombelSuffixes = (suffixes: string[]) => {
    const bersih = suffixes.map((s) => s.trim()).filter(Boolean);
    if (bersih.length === 0) return;
    if (!formData.label.trim()) {
      error('Isi Nama Tingkat dulu, nama rombel diambil dari nama tingkat.');
      return;
    }
    setRombelList((prev) => {
      const seen = new Set(prev.map((r) => namaRombel(r).toLowerCase()));
      const next = [...prev];
      for (const s of bersih) {
        const draft: RombelDraft = { suffix: s, terisi: 0 };
        const key = namaRombel(draft).toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          next.push(draft);
        }
      }
      return next;
    });
  };

  const handleAddRombelInput = () => {
    addRombelSuffixes(rombelInput.split(','));
    setRombelInput('');
  };

  const handleGenerate = () => {
    const jumlah = Math.min(26, Math.max(1, Number(jumlahOtomatis) || 1));
    addRombelSuffixes(Array.from({ length: jumlah }, (_, i) => String.fromCharCode(65 + i)));
  };

  const removeRombel = (index: number) => {
    setRombelList((prev) => prev.filter((_, i) => i !== index));
  };

  // ---------- Simpan tingkat + rombel ----------
  const saveMutation = useMutation({
    mutationFn: async (rombel: RombelDraft[]) => {
      // 1. Simpan tingkat
      if (editingItem) {
        await api.put(`/api/tingkat/${editingItem.id}`, formData);
      } else {
        await api.post('/api/tingkat', formData);
      }
      const kodeTingkat = editingItem ? editingItem.value : formData.value.trim();

      // 2. Sinkronkan kelas: hapus yang dibuang, buat yang baru
      const keptIds = new Set(rombel.filter((r) => r.id).map((r) => r.id));
      const toDelete = originalRombel.filter((r) => r.id && !keptIds.has(r.id));
      const toCreate = rombel.filter((r) => !r.id);

      const gagal: string[] = [];
      const jalankan = async (label: string, fn: () => Promise<unknown>) => {
        try {
          await fn();
        } catch (err: any) {
          gagal.push(`${label}: ${err?.message || 'gagal'}`);
        }
      };

      for (const r of toDelete) {
        await jalankan(r.nama || '', () => api.delete(`/api/kelas/${r.id}`));
      }
      for (const r of toCreate) {
        const nama = `${formData.label.trim()} ${r.suffix}`.trim();
        await jalankan(nama, () =>
          api.post('/api/kelas', {
            tingkat: kodeTingkat,
            nama,
            tahun_ajaran_id: activeTa?.id,
            wali_kelas_id: null,
            kapasitas: KAPASITAS_DEFAULT,
          })
        );
      }

      return { gagal };
    },
    onSuccess: ({ gagal }) => {
      refresh();
      setModalOpen(false);
      if (gagal.length > 0) {
        error(
          `Tingkat tersimpan, tetapi ${gagal.length} rombel gagal diproses: ${gagal
            .slice(0, 3)
            .join('; ')}${gagal.length > 3 ? ' ...' : ''}`
        );
      } else {
        success(
          editingItem
            ? 'Tingkat kelas berhasil diperbarui.'
            : 'Tingkat kelas berhasil ditambahkan.'
        );
      }
    },
    onError: (err: any) => error(err.message || 'Gagal menyimpan tingkat.'),
  });

  // Hapus tingkat: rombelnya dihapus dulu (backend menolak tingkat yang masih dipakai kelas)
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const tingkat = list.find((t) => t.id === id);
      if (tingkat) {
        const kelas = await fetchKelasTingkat(tingkat.value);
        for (const k of kelas) {
          if (k.id) await api.delete(`/api/kelas/${k.id}`);
        }
      }
      await api.delete(`/api/tingkat/${id}`);
    },
    onSuccess: () => {
      refresh();
      success('Tingkat kelas berhasil dihapus.');
      setDeleteId(null);
    },
    onError: (err: any) => {
      refresh();
      error(err.message || 'Gagal menghapus tingkat.');
      setDeleteId(null);
    },
  });

  const handleOpenModal = async (item?: TingkatRow) => {
    setRombelInput('');
    setJumlahOtomatis(4);
    setRombelList([]);
    setOriginalRombel([]);

    if (item) {
      setEditingItem(item);
      setFormData({
        value: item.value,
        label: item.label,
        jenjang: item.jenjang || '',
        urutan: item.urutan,
      });
      setModalOpen(true);
      setLoadingRombel(true);
      try {
        const kelas = await fetchKelasTingkat(item.value);
        setRombelList(kelas);
        setOriginalRombel(kelas.map((r) => ({ ...r })));
      } catch (err: any) {
        error(err.message || 'Gagal memuat daftar rombel.');
      } finally {
        setLoadingRombel(false);
      }
    } else {
      setEditingItem(null);
      const nextUrutan = list.length > 0 ? Math.max(...list.map((t) => t.urutan)) + 1 : 1;
      setFormData({ value: '', label: '', jenjang: '', urutan: nextUrutan });
      setModalOpen(true);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.value.trim() || !formData.label.trim()) {
      error('Kode dan nama tingkat wajib diisi.');
      return;
    }

    // Masukkan juga rombel yang masih diketik tapi belum ditekan Tambah
    const merged = [...rombelList];
    const seen = new Set(merged.map((r) => namaRombel(r).toLowerCase()));
    for (const s of rombelInput.split(',').map((x) => x.trim()).filter(Boolean)) {
      const draft: RombelDraft = { suffix: s, terisi: 0 };
      const key = namaRombel(draft).toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(draft);
      }
    }

    if (merged.some((r) => !r.id) && !activeTa) {
      error('Belum ada tahun ajaran. Buat tahun ajaran dulu sebelum menambah rombel.');
      return;
    }

    saveMutation.mutate(merged);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Tingkat Kelas</h2>
          <p className="text-xs text-slate-500 mt-1">
            Atur tingkat beserta rombelnya (A, B, C, ...). Rombel otomatis menjadi data di Kelas /
            Rombel, tempat memilih wali kelas dan tahun ajaran.
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-semibold shadow-xs transition-colors cursor-pointer self-start sm:self-auto"
          >
            <Plus className="w-4 h-4" />
            Tambah Tingkat
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
        {isLoading ? (
          <div className="p-6">
            <TableSkeleton rows={4} cols={5} />
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            title="Belum Ada Tingkat"
            description="Tambahkan tingkat kelas beserta rombelnya, misalnya 1 IBT dengan rombel A, B, C."
            onAction={canCreate ? () => handleOpenModal() : undefined}
            actionText="Tambah Tingkat"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[10px] sm:text-xs">
  <thead className="bg-slate-50 border-b border-slate-200/80 text-slate-600 font-semibold uppercase tracking-tight sm:tracking-wider text-[9px] sm:text-[11px]">
    <tr>
  <th className="hidden sm:table-cell py-3.5 px-4 text-center">Urutan</th>
  <th className="py-3 pl-4 pr-1.5 sm:py-3.5 sm:px-4 text-center">Kode</th>
  <th className="py-3 px-1.5 sm:py-3.5 sm:px-4">Nama Tingkat</th>
  <th className="py-3 px-1.5 sm:py-3.5 sm:px-4 text-center">Jenjang</th>
  <th className="py-3 px-1.5 sm:py-3.5 sm:px-4">Rombel</th>
  <th className="py-3 px-2 sm:py-3.5 sm:px-4 text-center">Aksi</th>
</tr>
  </thead>
  <tbody className="divide-y divide-slate-100 text-slate-700">
    {list.map((item) => {
      const rombel = rombelPerTingkat(item.value);
      return (
        <tr key={item.id} className="hover:bg-slate-50/70 transition-colors">
          <td className="hidden sm:table-cell py-3.5 px-4 font-mono text-slate-500 text-center">
  {item.urutan}
</td>
<td className="py-2.5 pl-4 pr-1.5 sm:py-3.5 sm:px-4 font-mono font-bold text-slate-900 text-center">
  {item.value}
</td>
          <td className="py-2.5 px-1.5 sm:py-3.5 sm:px-4 font-semibold text-slate-900 whitespace-nowrap">
            {item.label}
          </td>
          <td className="py-2.5 px-1.5 sm:py-3.5 sm:px-4 text-center">
  {item.jenjang ? (
    <span className="inline-block px-1.5 sm:px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-800 font-semibold whitespace-nowrap">
      {item.jenjang}
    </span>
  ) : (
    <span className="text-slate-400">-</span>
  )}
</td>
          <td className="py-2.5 px-1.5 sm:py-3.5 sm:px-4">
            {rombel.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {rombel.slice(0, 4).map((nama) => (
                  <span
                    key={nama}
                    className="px-1.5 sm:px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-semibold whitespace-nowrap"
                  >
                    {nama}
                  </span>
                ))}
                {rombel.length > 4 && (
                  <span
                    className="px-1.5 sm:px-2 py-0.5 rounded-md bg-slate-50 text-slate-500 font-semibold"
                    title={rombel.slice(4).join(', ')}
                  >
                    +{rombel.length - 4}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-slate-400">-</span>
            )}
          </td>
          <td className="py-2.5 px-2 sm:py-3.5 sm:px-4 text-center">
  <div className="flex items-center justify-center gap-0 sm:gap-1.5">
              {canEdit && (
                <button
                  onClick={() => handleOpenModal(item)}
                  className="p-1 sm:p-1.5 text-slate-500 hover:text-emerald-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                  title="Ubah tingkat & rombel"
                >
                  <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => setDeleteId(item.id)}
                  className="p-1 sm:p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                  title="Hapus"
                >
                  <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
              )}
            </div>
          </td>
        </tr>
      );
    })}
  </tbody>
</table>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingItem ? 'Ubah Tingkat Kelas' : 'Tambah Tingkat Kelas'}
        maxWidth="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Kode Tingkat
              </label>
              <input
                type="text"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder="Contoh: 1"
                disabled={Boolean(editingItem)}
                className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:outline-emerald-600 font-mono disabled:bg-slate-100 disabled:text-slate-500"
                required
              />
              {editingItem && (
                <p className="text-[11px] text-slate-400 mt-1">
                  Kode tidak bisa diubah karena sudah dipakai data lain.
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Nama Tingkat
              </label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="Contoh: 1 IBT"
                className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:outline-emerald-600"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Jenjang (Opsional)
              </label>
              <input
                type="text"
                value={formData.jenjang}
                onChange={(e) => setFormData({ ...formData, jenjang: e.target.value })}
                placeholder="Contoh: IBT, WUSTHO"
                className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:outline-emerald-600"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Urutan</label>
              <input
                type="number"
                min={0}
                value={formData.urutan}
                onChange={(e) => setFormData({ ...formData, urutan: Number(e.target.value) })}
                className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:outline-emerald-600"
              />
            </div>
          </div>

          {/* Daftar Rombel */}
          <div className="pt-3 border-t border-slate-100">
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Daftar Rombel
            </label>

            {loadingRombel ? (
              <p className="text-xs text-slate-400 mb-2">Memuat daftar rombel...</p>
            ) : (
              rombelList.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {rombelList.map((r, i) => (
                    <span
                      key={r.id ?? `baru-${r.suffix}`}
                      className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold"
                    >
                      {namaRombel(r)}
                      {r.terisi > 0 && (
                        <span className="text-[10px] font-medium text-emerald-700">
                          ({r.terisi})
                        </span>
                      )}
                      <button
                        type="button"
                        disabled={r.terisi > 0}
                        onClick={() => removeRombel(i)}
                        className="p-0.5 rounded hover:bg-slate-200 text-slate-500 disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer disabled:cursor-not-allowed"
                        title={
                          r.terisi > 0
                            ? `Rombel berisi ${r.terisi} siswa, pindahkan dulu`
                            : 'Hapus rombel'
                        }
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )
            )}

            <div className="flex gap-2">
              <input
                type="text"
                value={rombelInput}
                onChange={(e) => setRombelInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    handleAddRombelInput();
                  }
                }}
                placeholder="Ketik rombel lalu Enter, contoh: A"
                className="flex-1 px-3.5 py-2 text-sm border border-slate-300 rounded-xl focus:outline-emerald-600"
              />
              <button
                type="button"
                onClick={handleAddRombelInput}
                className="px-3.5 py-2 text-xs font-semibold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded-xl cursor-pointer"
              >
                Tambah
              </button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Nama kelas dibentuk dari nama tingkat + rombel, contoh: {formData.label || '1 IBT'} A.
              Angka hijau = jumlah siswa di rombel itu.
            </p>

            <div className="mt-3 flex flex-wrap items-end gap-2 p-3 rounded-xl bg-slate-50 border border-slate-200">
              <div>
                <label className="block text-[10px] text-slate-500 mb-0.5">Jumlah rombel</label>
                <input
                  type="number"
                  min={1}
                  max={26}
                  value={jumlahOtomatis}
                  onChange={(e) => setJumlahOtomatis(Number(e.target.value))}
                  className="w-24 px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-emerald-600 bg-white"
                />
              </div>
              <button
                type="button"
                onClick={handleGenerate}
                className="px-3.5 py-1.5 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 rounded-lg cursor-pointer"
              >
                Buat rombel A sampai{' '}
                {String.fromCharCode(
                  64 + Math.min(26, Math.max(1, Number(jumlahOtomatis) || 1))
                )}
              </button>
            </div>
          </div>

          <div className="pt-4 flex items-center justify-end gap-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-xl"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saveMutation.isPending || loadingRombel}
              className="px-4 py-2 text-xs font-semibold text-white bg-emerald-700 hover:bg-emerald-800 disabled:bg-emerald-400 rounded-xl transition-colors cursor-pointer"
            >
              {saveMutation.isPending
                ? 'Menyimpan...'
                : editingItem
                ? 'Simpan Perubahan'
                : 'Tambah'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        title="Hapus Tingkat Kelas"
        message="Tingkat beserta seluruh rombelnya akan dihapus. Tingkat yang rombelnya masih berisi siswa, atau masih dipakai tarif pembayaran, tidak dapat dihapus."
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}